import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, BookOpen, Music2, LayoutGrid, ArrowLeft, Bell } from "lucide-react";
import { useI18n, LANGS } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import OfflineBanner from "@/components/OfflineBanner";
import EditableChurchName from "@/components/EditableChurchName";
import MusicButton from "@/components/MusicButton";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function TopBar({ title, backTo, eyebrow }) {
  const navigate = useNavigate();
  const { church, user } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user || user.role === "super_admin") return;
    const fetchUnread = () =>
      api.get("/notifications/unread-count")
        .then((r) => setUnread(r.data.count || 0))
        .catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, [user]);
  return (
    <header className="sticky-top px-5 pt-4 pb-3" data-testid="top-bar">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {backTo ? (
            <button
              onClick={() => navigate(backTo)}
              data-testid="back-button"
              className="rounded-full w-9 h-9 grid place-items-center border border-stone-200 bg-white hover:bg-stone-50 transition"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          ) : church?.logo_url ? (
            <img
              src={church.logo_url}
              alt={church?.name || "Church logo"}
              data-testid="church-logo"
              className="w-10 h-10 rounded-full object-contain bg-white border border-stone-200 p-0.5 shrink-0"
            />
          ) : (
            <div
              className="w-9 h-9 rounded-full grid place-items-center text-white font-semibold tenant-bg shrink-0"
              data-testid="church-monogram"
            >
              {church?.name?.[0] ?? "C"}
            </div>
          )}
          <div className="min-w-0">
            {eyebrow && <div className="label-eyebrow">{eyebrow}</div>}
            {title ? (
              <h1 className="font-serif-display text-lg sm:text-xl truncate">{title}</h1>
            ) : (
              <EditableChurchName>
                <h1 className="font-serif-display text-lg sm:text-xl truncate">
                  {church?.name || t("appName")}
                </h1>
              </EditableChurchName>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="language-switcher"
              className="rounded-full px-3 h-9 grid place-items-center border border-stone-200 bg-white text-xs font-semibold tracking-wider"
            >
              {LANGS.find((l) => l.id === lang)?.short || "EN"}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel>{t("language")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {LANGS.map((l) => (
                <DropdownMenuItem
                  key={l.id}
                  data-testid={`lang-${l.id}`}
                  onClick={() => setLang(l.id)}
                  className={lang === l.id ? "font-semibold" : ""}
                >
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Link
            to="/notifications"
            data-testid="header-bell"
            className="rounded-full w-9 h-9 grid place-items-center border border-stone-200 bg-white hover:bg-stone-50 transition relative"
          >
            <Bell className="w-4 h-4" />
            {unread > 0 && (
              <span
                data-testid="bell-unread-badge"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full text-[10px] font-bold text-white"
                style={{ backgroundColor: "var(--tenant-accent)" }}
              >
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </Link>
        </div>
      </div>
    </header>
  );
}

export function BottomNav() {
  const loc = useLocation();
  const { t } = useI18n();
  const items = [
    { to: "/", icon: Home, label: t("home"), id: "home" },
    { to: "/bible", icon: BookOpen, label: t("bible"), id: "bible" },
    { to: "/songbook", icon: Music2, label: t("songbook"), id: "songbook" },
    { to: "/more", icon: LayoutGrid, label: t("more"), id: "more" },
  ];
  return (
    <nav className="bottom-nav" data-testid="bottom-nav">
      <div className="mx-auto max-w-[480px] px-3 py-2 flex justify-around">
        {items.map((it) => {
          const active = loc.pathname === it.to;
          const Icon = it.icon;
          const node = (
            <Link
              key={it.id}
              to={it.to}
              data-testid={`bottomnav-${it.id}`}
              className="flex flex-col items-center gap-1 py-1 px-3 min-w-[56px]"
            >
              <Icon
                className="w-5 h-5"
                style={{ color: active ? "var(--tenant-primary)" : "#78716C" }}
                strokeWidth={active ? 2.2 : 1.6}
              />
              <span
                className="text-[10px] font-medium tracking-wide"
                style={{ color: active ? "var(--tenant-primary)" : "#78716C" }}
              >
                {it.label}
              </span>
            </Link>
          );
          // Slot the Music tab between "songbook" and "more" so the
          // order reads: Home · Bible · Songbook · Music · More.
          if (it.id === "more") {
            return (
              <React.Fragment key={it.id}>
                <MusicButton />
                {node}
              </React.Fragment>
            );
          }
          return node;
        })}
      </div>
    </nav>
  );
}

export default function Layout({ children, title, backTo, eyebrow, hideNav, hideTop }) {
  return (
    <div className="mobile-shell" data-testid="mobile-shell">
      <OfflineBanner />
      {!hideTop && <TopBar title={title} backTo={backTo} eyebrow={eyebrow} />}
      <main className="fade-in pb-4">{children}</main>
      {!hideNav && <BottomNav />}
    </div>
  );
}
